/**
 * HeadlessClient — Event-driven controller for custom/native website UIs
 *
 * Encapsulates:
 * - Fingerprinting & visitor identification
 * - Config fetching & domain validation
 * - LiveKit room connection & audio/text channels
 * - Real-time speech transcription & RAG image citations
 * - Session status beacons & conversation sync to inbox
 */
import { ApiClient } from './api.js';
import { generateId } from './utils.js';
import { collectFingerprint, buildCookieSnapshot } from './fingerprint.js';
import { JourneyTracker } from './tracker.js';
import { SessionRecorder } from './replay.js';

export class HeadlessClient {
    constructor(options = {}) {
        this.options = options;
        this.appId = options.appId;
        this.configUrl = (options.configUrl || options.server || '').replace(/\/+$/, '');
        this.api = new ApiClient({ appId: this.appId, configUrl: this.configUrl });
        
        this.config = null;
        this.identity = null;
        this.roomName = null;
        this.lk = null;
        this.state = 'idle'; // 'idle' | 'connecting' | 'text' | 'voice'
        this._listeners = new Map();
        this._currentMode = 'text';

        this._onBeforeUnload = () => {
            if (this.roomName && this.identity) {
                this.api.sendSessionEndedBeacon({
                    visitorId: this.identity,
                    channel: this._currentMode === 'voice' ? 'voice' : 'chat',
                    roomName: this.roomName,
                });
            }
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', this._onBeforeUnload);
        }
    }

    // ── Event Emitter ─────────────────────────────────

    on(event, handler) {
        if (!this._listeners.has(event)) {
            this._listeners.set(event, new Set());
        }
        this._listeners.get(event).add(handler);
        return () => this.off(event, handler);
    }

    off(event, handler) {
        if (this._listeners.has(event)) {
            this._listeners.get(event).delete(handler);
        }
    }

    emit(event, ...args) {
        if (this._listeners.has(event)) {
            for (const handler of this._listeners.get(event)) {
                try { handler(...args); } catch (err) { console.error(`[iGreet Headless] Error in listener for ${event}:`, err); }
            }
        }
    }

    // ── Lifecycle ─────────────────────────────────────

    async init() {
        try {
            this.config = await this.api.fetchConfig();
            this.emit('config', this.config);

            // Check domain whitelist
            if (!this._isDomainAllowed()) {
                console.warn('[iGreet Headless] Domain not authorized:', window.location.hostname);
                this.emit('error', new Error('Domain not authorized'));
                return false;
            }

            // Identify visitor
            await this._identifyVisitor();

            // Init journey tracker if enabled
            if (this.config.journeyEnabled !== false && this.options.journey?.enabled !== false) {
                this.tracker = new JourneyTracker({
                    appId: this.appId,
                    apiUrl: this.configUrl || window.location.origin,
                    visitorId: this.identity,
                    journey: this.options.journey,
                });
                this.tracker.start();
            }

            // Init session replay recorder if enabled
            const replayEnabled = this.config.replayEnabled !== false && this.options.replay?.enabled !== false;
            if (replayEnabled) {
                const sessionId = this.tracker?.sessionId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('__gr_sid') : '') || `s_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
                this.recorder = new SessionRecorder({
                    appId: this.appId,
                    serverUrl: this.configUrl || window.location.origin,
                    sessionId,
                    visitorId: this.identity,
                    ...this.options.replay,
                });
                this.recorder.start();
            }

            this.emit('ready', { config: this.config, identity: this.identity });
            return true;
        } catch (err) {
            console.error('[iGreet Headless] Init failed:', err);
            this.emit('error', err);
            return false;
        }
    }

    // ── Communication ─────────────────────────────────

    async sendMessage(text) {
        if (!text || !text.trim()) return;
        const cleanText = text.trim();

        // Emit user message event immediately
        this.emit('message', {
            role: 'user',
            content: cleanText,
            time: new Date(),
        });

        // Persist interaction to inbox
        this.api.persistInteraction({
            visitorId: this.identity,
            role: 'consumer',
            content: cleanText,
            type: this._currentMode === 'voice' ? 'voice' : 'text',
        });

        if (!this.lk) {
            await this._connect();
        }

        if (this.lk) {
            this.emit('typing', true);
            await this.lk.sendText(cleanText);
        }
    }

    async toggleMic() {
        if (!this.lk) {
            await this._connect();
        }
        if (this.lk) {
            const isEnabled = this.lk.isMicEnabled();
            if (isEnabled) {
                await this.lk.disableMicrophone();
                this.lk.muteAgentAudio();
                this.emit('voiceState', { listening: false });
            } else {
                await this.lk.enableMicrophone();
                this.lk.unmuteAgentAudio();
                this.emit('voiceState', { listening: true });
            }
            return !isEnabled;
        }
        return false;
    }

    async setMode(mode) {
        if (mode === this._currentMode && this.lk) return;
        this._currentMode = mode;

        if (!this.lk) {
            await this._connect();
        }

        if (this.lk) {
            if (mode === 'voice') {
                await this.lk.enableMicrophone();
                this.lk.unmuteAgentAudio();
                this.emit('voiceState', { listening: true });
            } else {
                await this.lk.disableMicrophone();
                this.lk.muteAgentAudio();
                this.emit('voiceState', { listening: false });
            }
        }

        this.emit('modeChange', mode);
        if (this.roomName) {
            this.api.notifySessionStatus({
                visitorId: this.identity,
                status: 'active',
                channel: mode === 'voice' ? 'voice' : 'chat',
                roomName: this.roomName,
            });
        }
    }

    async resolveImage(filename) {
        return this.api.resolveImage(filename);
    }

    disconnect() {
        if (this.lk) {
            this.lk.disconnect();
            this.lk = null;
        }
        this._setState('idle');
        this.emit('disconnected');
    }

    // ── Internal Helpers ──────────────────────────────

    async _identifyVisitor() {
        if (this.identity) return;
        try {
            const rawJsFp = await collectFingerprint();
            const cookieSnapshot = buildCookieSnapshot();
            const result = await this.api.identify({ rawJsFp, cookieSnapshot });
            this.identity = result.visitorId;
        } catch (err) {
            this.identity = `visitor_${generateId()}`;
        }
    }

    async _connect() {
        if (this.lk) return;
        this._setState('connecting');

        try {
            this.roomName = `widget_${this.appId}_${generateId()}`;
            const tokenData = await this.api.fetchToken({
                identity: this.identity,
                roomName: this.roomName,
            });

            const { LiveKitConnection } = await import('./livekit.js');
            this.lk = new LiveKitConnection({
                token: tokenData.token,
                url: tokenData.url,
                onMessage: async (msg) => {
                    // In text mode, user message was already emitted locally on sendMessage()
                    if (msg.role === 'user' && this._currentMode !== 'voice') {
                        return;
                    }

                    this.emit('typing', false);
                    this.emit('message', msg);

                    // Check for image citations
                    const imageMatches = [...(msg.content || '').matchAll(/\[Image:\s*([^\]]+)\]/gi)];
                    if (imageMatches.length > 0) {
                        for (const match of imageMatches) {
                            const filename = (match[1] || '').trim();
                            try {
                                const resolved = await this.resolveImage(filename);
                                this.emit('image', { token: match[0], filename, ...resolved });
                            } catch (e) {
                                console.warn('[iGreet Headless] Image resolution error:', filename, e);
                            }
                        }
                    }

                    // Persist bot responses
                    if (msg.content && msg.role !== 'user') {
                        this.api.persistInteraction({
                            visitorId: this.identity,
                            role: msg.role === 'assistant' ? 'bot' : msg.role,
                            content: msg.content,
                        });
                    }
                },
                onStateChange: (state) => {
                    this.emit('livekitState', state);
                    if (state === 'connected') {
                        this._setState(this._currentMode === 'voice' ? 'voice' : 'text');
                    } else if (state === 'disconnected') {
                        this._setState('idle');
                    }
                },
                onTyping: () => {
                    this.emit('typing', true);
                },
            });

            await this.lk.connect();
        } catch (err) {
            console.error('[iGreet Headless] Connection error:', err);
            this._setState('idle');
            this.emit('error', err);
        }
    }

    _setState(newState) {
        const prev = this.state;
        this.state = newState;
        this.emit('stateChange', { state: newState, prev });
    }

    _isDomainAllowed() {
        const allowed = this.config?.allowedDomains;
        if (!allowed || allowed.length === 0) return true;
        const hostname = window.location.hostname.toLowerCase();
        if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
        return allowed.some(d => {
            try {
                const norm = d.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();
                return hostname === norm || hostname.endsWith(`.${norm}`);
            } catch { return false; }
        });
    }

    destroy() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this._onBeforeUnload);
        }
        if (this.recorder) {
            this.recorder.stop();
            this.recorder = null;
        }
        if (this.tracker) {
            this.tracker.destroy();
            this.tracker = null;
        }
        if (this.lk) {
            try { this.lk.disconnect(); } catch {}
            this.lk = null;
        }
        this._listeners.clear();
        this.state = 'idle';
    }
}
